import React, {useState} from 'react';

import {ReplaceTableColumnMapping} from 'analysis-tool/data-frame-creation/column-mapping/ReplaceTableColumnMapping';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput} from 'pipelines/network/PipelineDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import RightSidebarSkeleton, {RightSidebarWidth} from '../components/RightSidebarSkeleton';
import {getPreviewDataFrameColumns} from './ReplaceSourceUtils';

interface ColumnMappingPhaseProps {
  selectedStepType: PipelineStepType;
  templateDataFrameColumns: Array<DataFrameColumnDef>;
  previewOutput: PreviewOutput | null;
  isPreviewLoading: boolean;
  previewError: string | null;
  onBack: () => void;
  onSubmit: (selectedColumns: Array<DataFrameColumnDef | null>) => Promise<void>;
}

export default function ColumnMappingPhase({
  selectedStepType,
  templateDataFrameColumns,
  previewOutput,
  isPreviewLoading,
  previewError,
  onBack,
  onSubmit,
}: ColumnMappingPhaseProps): JSX.Element {
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);

  const newDataFrameColumns = getPreviewDataFrameColumns(previewOutput, selectedStepType);
  const columnsReady = newDataFrameColumns != null;

  return (
    <RightSidebarSkeleton
      title="Map columns (optional)"
      confirmationButtons={[
        {
          text: 'Back',
          onClick: onBack,
          dataTestAttrsElement: 'back',
        },
        {
          text: 'Replace table',
          onClick: () => onSubmit(selectedColumns),
          dataTestAttrsElement: 'replaceTable',
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      useAbsolutePositioning={true}
      dataTestAttrs={{
        component: 'ReplaceSourceStepRightSidebar',
        element: 'columnMapping',
      }}
    >
      <div>
        {columnsReady ? (
          <ReplaceTableColumnMapping
            templateDataFrameColumns={templateDataFrameColumns}
            newDataFrameColumns={newDataFrameColumns}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            isManuallyChanged={isManuallyChanged}
            setIsManuallyChanged={setIsManuallyChanged}
            ignoreColumnValidationErrors={true}
          />
        ) : (
          <>
            {isPreviewLoading && <CenteredHorizontalThrobber />}
            {previewError != null && (
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={
                  /cannot be previewed/i.test(previewError)
                    ? `Cannot map columns when using "${STEP_TYPE_TO_USER_FACING_NAME[selectedStepType]}" to replace a table.`
                    : previewError
                }
              />
            )}
          </>
        )}
      </div>
    </RightSidebarSkeleton>
  );
}
